import { ComponentType } from "react";
import { FixedSizeList, FixedSizeListProps, ListChildComponentProps } from "react-window";

interface ILazyRenderingFixedListProps<T> {
    /**
     * List of items to be rendered.
     *
     * T represents the type of each item in the array.
     */
    items: ReadonlyArray<T>;

    /**
     * Component that will render each row.
     *
     * `row` will receive props of type ListChildComponentProps, which includes `index`, `style`, etc.
     */
    row: ComponentType<ListChildComponentProps<ReadonlyArray<T>>>;

    /**
     * The width of the list container.
     * This is passed to the `FixedSizeList` component from react-window.
     */
    width: FixedSizeListProps["width"];

    /**
     * The height of the list container.
     * This is passed to the `FixedSizeList` component from react-window.
     */
    height: FixedSizeListProps["height"];

    /**
     * The size of each item in the list (in pixels).
     * This is used by `react-window` to determine the height of each row in the list.
     */
    itemSize: FixedSizeListProps["itemSize"];
}

/**
 * A generic React component that efficiently renders a large list of items using lazy rendering techniques.
 * It uses `FixedSizeList` from the [react-window](https://www.npmjs.com/package/react-window) library to render
 * only the visible items based on the current scroll position, which improves performance for large lists.
 */
function LazyRenderingFixedList<T>(props: Readonly<ILazyRenderingFixedListProps<T>>): JSX.Element {
    const { items, row, width, height, itemSize } = props;

    if (!items.length) {
        return null;
    }

    return (
        <FixedSizeList
            itemCount={items.length}
            itemData={items}
            width={width}
            height={height}
            itemSize={itemSize}
        >
            {row}
        </FixedSizeList>

    );
}

export { LazyRenderingFixedList };
